import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FormulaDestacada, type GameMode } from '@interactive-room';

import { BuilderInput, BuilderSelect, BuilderTextarea } from '../BuilderField';
import { WikiImagePicker } from './WikiImagePicker';

interface EditorialEditorProps {
  payload: Record<string, unknown>;
  label?: string;
  onPayloadChange: (payload: Record<string, unknown>) => void;
  onTitleChange?: (title: string) => void;
  subjectTag?: string;
}

const DESTACADO_TONE_OPTIONS = [
  { value: 'mint', label: 'Verde · idea clave' },
  { value: 'lavender', label: 'Lavanda · contexto' },
  { value: 'amber', label: 'Ámbar · atención' },
  { value: 'neutral', label: 'Neutro' },
];

/** Editor de sección / subtítulo — tipografía de documento, sin cajas anidadas. */
export function SubtituloBlockEditor({
  payload,
  label,
  onPayloadChange,
  onTitleChange,
}: EditorialEditorProps) {
  const title = String(payload.title ?? label ?? '');
  const subtitle = String(payload.subtitle ?? '');

  return (
    <div className="guide-block-editor">
      <BuilderInput
        label="Título de la sección"
        hint="Cómo se verá el encabezado en la guía impresa."
        value={title}
        onChange={(e) => {
          const next = e.target.value;
          onPayloadChange({ ...payload, title: next });
          onTitleChange?.(next);
        }}
        placeholder="Ej: Contexto histórico"
      />
      <BuilderInput
        label="Subtítulo (opcional)"
        value={subtitle}
        onChange={(e) => onPayloadChange({ ...payload, subtitle: e.target.value })}
        placeholder="Una línea que encuadre la sección"
      />
      {title.trim() ? (
        <div className="guide-block-editor__preview" aria-hidden>
          <p className="guide-block-editor__preview-kicker">Así se verá</p>
          <h3 className="guide-block-editor__preview-title">{title.trim()}</h3>
          {subtitle.trim() ? (
            <p className="guide-block-editor__preview-muted">{subtitle.trim()}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Caja destacada / idea clave. */
export function DestacadoBlockEditor({
  payload,
  label,
  onPayloadChange,
  onTitleChange,
}: EditorialEditorProps) {
  const title = String(payload.title ?? label ?? '');
  const body = String(payload.body ?? '');
  const tone = String(payload.tone ?? 'mint');

  return (
    <div className="guide-block-editor">
      <BuilderInput
        label="Título del destacado"
        value={title}
        onChange={(e) => {
          const next = e.target.value;
          onPayloadChange({ ...payload, title: next });
          onTitleChange?.(next);
        }}
        placeholder="Ej: Sabías que…"
      />
      <BuilderTextarea
        label="Texto"
        hint="Idea clave, dato o síntesis. Podés usar **negritas**."
        value={body}
        onChange={(e) => onPayloadChange({ ...payload, body: e.target.value })}
        placeholder="Escribe el contenido que el estudiante debe retener…"
        className="min-h-[120px]"
      />
      <BuilderSelect
        label="Énfasis visual"
        value={tone}
        options={DESTACADO_TONE_OPTIONS}
        onChange={(e) => onPayloadChange({ ...payload, tone: e.target.value })}
      />
    </div>
  );
}

/** Recurso visual / figura. */
export function FiguraBlockEditor({
  payload,
  label,
  onPayloadChange,
  onTitleChange,
}: EditorialEditorProps) {
  const title = String(payload.title ?? label ?? '');
  const altText = String(payload.altText ?? '');
  const caption = String(payload.caption ?? '');
  const imageUrl = String(payload.imageUrl ?? '');
  const imagePrompt = String(payload.imagePrompt ?? '');
  const wikiQuery = String(payload.wikiQuery ?? '');

  return (
    <div className="guide-block-editor">
      <BuilderInput
        label="Título (opcional)"
        value={title}
        onChange={(e) => {
          const next = e.target.value;
          onPayloadChange({ ...payload, title: next });
          onTitleChange?.(next);
        }}
        placeholder="Ej: Mapa de la ruta"
      />
      <BuilderInput
        label="¿Qué muestra la imagen?"
        hint="Descripción breve para accesibilidad y para buscar/generar la imagen."
        value={altText}
        onChange={(e) => onPayloadChange({ ...payload, altText: e.target.value })}
        placeholder="Ej: Mapa de la ruta de Almagro desde el Cuzco"
      />
      <BuilderTextarea
        label="Pie de imagen / consigna visual"
        hint="Qué debe observar o pensar el estudiante al mirar la figura."
        value={caption}
        onChange={(e) => onPayloadChange({ ...payload, caption: e.target.value })}
        placeholder="Observa… / Identifica…"
      />
      {imageUrl ? (
        <div className="guide-block-editor__preview">
          <p className="guide-block-editor__preview-kicker">Imagen actual</p>
          <img
            src={imageUrl}
            alt={altText || title || 'Recurso visual'}
            className="guide-block-editor__figure"
          />
        </div>
      ) : null}
      {wikiQuery ? (
        <WikiImagePicker
          wikiQuery={wikiQuery}
          currentImageUrl={imageUrl}
          onSelect={(url) => onPayloadChange({ ...payload, imageUrl: url, wikiQuery })}
        />
      ) : null}
      {wikiQuery ? (
        <BuilderInput
          label="Búsqueda Wikimedia"
          hint="Término usado para encontrar la imagen. Puedes editarlo y volver a elegir."
          value={wikiQuery}
          onChange={(e) => onPayloadChange({ ...payload, wikiQuery: e.target.value })}
          placeholder="Ej: Acta Independencia Chile 1818"
        />
      ) : (
        <BuilderInput
          label="Prompt de imagen (opcional)"
          hint="Instrucción breve en inglés para regenerar la imagen con IA."
          value={imagePrompt}
          onChange={(e) => onPayloadChange({ ...payload, imagePrompt: e.target.value })}
          placeholder="historical map of Atacama desert expedition, educational"
        />
      )}
    </div>
  );
}

/** Glosario en tabla — filas editables, sin JSON. */
export function GlosarioTablaBlockEditor({
  payload,
  label,
  onPayloadChange,
  onTitleChange,
}: EditorialEditorProps) {
  const title = String(payload.title ?? label ?? 'Glosario');
  const entries = Array.isArray(payload.entries)
    ? (payload.entries as Array<{ term?: string; definition?: string }>)
    : [];

  const updateEntry = (index: number, patch: { term?: string; definition?: string }) => {
    const next = entries.map((entry, i) =>
      i === index
        ? {
            term: patch.term !== undefined ? patch.term : String(entry.term ?? ''),
            definition:
              patch.definition !== undefined ? patch.definition : String(entry.definition ?? ''),
          }
        : {
            term: String(entry.term ?? ''),
            definition: String(entry.definition ?? ''),
          },
    );
    onPayloadChange({ ...payload, title, entries: next });
  };

  const addEntry = () => {
    onPayloadChange({
      ...payload,
      title,
      entries: [...entries.map((e) => ({ term: String(e.term ?? ''), definition: String(e.definition ?? '') })), { term: '', definition: '' }],
    });
  };

  const removeEntry = (index: number) => {
    const next = entries
      .filter((_, i) => i !== index)
      .map((e) => ({ term: String(e.term ?? ''), definition: String(e.definition ?? '') }));
    onPayloadChange({ ...payload, title, entries: next.length ? next : [{ term: '', definition: '' }] });
  };

  return (
    <div className="guide-block-editor">
      <BuilderInput
        label="Título del glosario"
        value={title}
        onChange={(e) => {
          const next = e.target.value;
          onPayloadChange({
            ...payload,
            title: next,
            entries: entries.map((e) => ({
              term: String(e.term ?? ''),
              definition: String(e.definition ?? ''),
            })),
          });
          onTitleChange?.(next);
        }}
      />

      <div className="guide-block-editor__stack">
        <p className="sala-label">Términos</p>
        {entries.length === 0 ? (
          <p className="text-xs text-[var(--sala-ink-muted)]">Todavía no hay términos. Agrega el primero.</p>
        ) : null}
        {entries.map((entry, index) => (
          <div key={index} className="guide-block-editor__entry">
            <div className="guide-block-editor__entry-head">
              <span className="guide-block-editor__entry-index">{index + 1}</span>
              <button
                type="button"
                className="guide-block-editor__icon-btn"
                onClick={() => removeEntry(index)}
                aria-label={`Quitar término ${index + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <BuilderInput
              label="Término"
              value={String(entry.term ?? '')}
              onChange={(e) => updateEntry(index, { term: e.target.value })}
              placeholder="Ej: Encomienda"
            />
            <BuilderTextarea
              label="Explicación"
              hint="Síntesis narrativa en lenguaje del estudiante — no definición de diccionario."
              value={String(entry.definition ?? '')}
              onChange={(e) => updateEntry(index, { definition: e.target.value })}
              className="min-h-[72px]"
            />
          </div>
        ))}
        <button type="button" className="guide-block-editor__add" onClick={addEntry}>
          <Plus className="mr-1.5 inline h-3.5 w-3.5" />
          Agregar término
        </button>
      </div>
    </div>
  );
}

/** Fórmula destacada con vista previa KaTeX. */
export function FormulaDestacadaBlockEditor({
  payload,
  label,
  onPayloadChange,
  onTitleChange,
  subjectTag,
}: EditorialEditorProps) {
  const title = String(payload.title ?? label ?? 'Fórmula');
  const latex = String(payload.latex ?? payload.formula ?? '');
  const description = String(payload.description ?? '');

  return (
    <div className="guide-block-editor">
      <BuilderInput
        label="Título"
        value={title}
        onChange={(e) => {
          const next = e.target.value;
          onPayloadChange({ ...payload, title: next, latex, description });
          onTitleChange?.(next);
        }}
      />
      <BuilderInput
        label="Fórmula (LaTeX)"
        hint="Sin delimitadores $ — ej: \\frac{a}{b} = c"
        value={latex}
        onChange={(e) =>
          onPayloadChange({ ...payload, title, latex: e.target.value, description })
        }
        placeholder="\\Delta\\% = \\frac{V_f - V_i}{V_i} \\times 100"
      />
      <BuilderTextarea
        label="Qué significa"
        hint="Explica las variables en lenguaje del estudiante."
        value={description}
        onChange={(e) =>
          onPayloadChange({ ...payload, title, latex, description: e.target.value })
        }
        className="min-h-[72px]"
      />
      {latex.trim() ? (
        <div className="guide-block-editor__preview">
          <p className="guide-block-editor__preview-kicker">Vista previa</p>
          <FormulaDestacada
            payload={{ title, latex, description }}
            subjectTag={subjectTag}
          />
        </div>
      ) : null}
    </div>
  );
}

const GUIDE_EDITORIAL_MODES = new Set([
  'subtitulo',
  'destacado',
  'figura',
  'glosario_tabla',
  'formula_destacada',
]);

export function isGuideEditorialMode(mode: string): boolean {
  return GUIDE_EDITORIAL_MODES.has(mode);
}

/** Despacha el editor pedagógico según el modo editorial. */
export function GuideEditorialBlockEditor({
  mode,
  payload,
  label,
  onPayloadChange,
  onTitleChange,
  subjectTag,
}: EditorialEditorProps & { mode: GameMode | string }) {
  switch (mode) {
    case 'subtitulo':
      return (
        <SubtituloBlockEditor
          payload={payload}
          label={label}
          onPayloadChange={onPayloadChange}
          onTitleChange={onTitleChange}
        />
      );
    case 'destacado':
      return (
        <DestacadoBlockEditor
          payload={payload}
          label={label}
          onPayloadChange={onPayloadChange}
          onTitleChange={onTitleChange}
        />
      );
    case 'figura':
      return (
        <FiguraBlockEditor
          payload={payload}
          label={label}
          onPayloadChange={onPayloadChange}
          onTitleChange={onTitleChange}
        />
      );
    case 'glosario_tabla':
      return (
        <GlosarioTablaBlockEditor
          payload={payload}
          label={label}
          onPayloadChange={onPayloadChange}
          onTitleChange={onTitleChange}
        />
      );
    case 'formula_destacada':
      return (
        <FormulaDestacadaBlockEditor
          payload={payload}
          label={label}
          onPayloadChange={onPayloadChange}
          onTitleChange={onTitleChange}
          subjectTag={subjectTag}
        />
      );
    default:
      return null;
  }
}

/** Snippet legible para la fila colapsada del accordion (sin jerga técnica). */
export function guideBlockCollapsedHint(mode: string, payload: Record<string, unknown>): string {
  switch (mode) {
    case 'subtitulo':
      return String(payload.subtitle || payload.title || 'Sección').slice(0, 90);
    case 'destacado':
      return String(payload.body || payload.title || 'Idea destacada').slice(0, 90);
    case 'figura':
      return String(payload.caption || payload.altText || 'Recurso visual').slice(0, 90);
    case 'glosario_tabla': {
      const entries = Array.isArray(payload.entries) ? payload.entries : [];
      if (!entries.length) return 'Sin términos aún';
      const terms = entries
        .slice(0, 3)
        .map((e) => String((e as { term?: string }).term || '').trim())
        .filter(Boolean);
      return terms.length ? terms.join(' · ') : `${entries.length} términos`;
    }
    case 'formula_destacada':
      return String(payload.description || payload.latex || 'Fórmula').slice(0, 90);
    case 'info_block':
      return String(payload.bodyText || payload.sectionTitle || payload.title || 'Lectura').slice(0, 90);
    case 'desarrollo':
      return String(payload.prompt || payload.title || 'Desarrollo').slice(0, 90);
    default:
      return '';
  }
}
